/** Sign in, create an account, reset a password, confirm an email address. */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck, Eye, EyeOff, LoaderCircle, UserRound } from "lucide-react";
import { APP_NAME, INVITATION_REWARD, planById } from "@brainlog/shared";
import { errorMessage, get, post } from "@/lib/api";
import { useConfig } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthCard, go } from "./PublicLayout";
import { choosePlan } from "@/features/settings/PlanSettings";
import { track } from "@/lib/analytics";
import { currentInvite, forgetInvite } from "./invite";

const params = () => new URLSearchParams(window.location.search);

/** Where to go after signing in: a page of this site only. */
function nextPath() {
  const n = params().get("next");
  return n && n.startsWith("/") && !n.startsWith("//") ? n : "/";
}
const enterApp = (path = nextPath()) => window.location.assign(path);

function Field(props: { id: string; label: string; type?: string; value: string; onChange(v: string): void; autoComplete?: string; hint?: string; autoFocus?: boolean }) {
  const [show, setShow] = useState(false);
  const password = props.type === "password";
  return (
    <div className="space-y-1.5">
      <Label htmlFor={props.id}>{props.label}</Label>
      <div className="relative">
        <Input
          id={props.id}
          type={password && show ? "text" : (props.type ?? "text")}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          autoComplete={props.autoComplete}
          autoFocus={props.autoFocus}
          required
          className={password ? "pr-10" : undefined}
        />
        {password && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
            aria-label={show ? "Hide password" : "Show password"}
          >
            {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        )}
      </div>
      {props.hint && <p className="text-xs text-muted-foreground">{props.hint}</p>}
    </div>
  );
}

function Problem({ text }: { text: string | null }) {
  return text ? (
    <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
      {text}
    </p>
  ) : null;
}

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = (fn: () => Promise<void>) => async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

const linkTo = (path: string, label: string) => (
  <a
    href={path}
    onClick={(e) => {
      e.preventDefault();
      go(path);
    }}
    className="font-medium text-primary underline-offset-2 hover:underline"
  >
    {label}
  </a>
);

/** Local development only: the seeded demo users. */
function DevUsers() {
  const config = useConfig();
  const users = useQuery({
    queryKey: ["dev-users"],
    queryFn: () => get<{ users: { id: string; displayName: string }[] }>("/api/dev/users"),
    enabled: !!config.data?.devIdentity,
  });
  if (!config.data?.devIdentity || !users.data?.users.length) return null;
  return (
    <div className="mt-6 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm" role="note">
      <p className="font-medium">Local development: demo users</p>
      <p className="text-xs text-muted-foreground">Not a real sign-in; this only exists on a development machine.</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {users.data.users.map((u) => (
          <Button
            key={u.id}
            size="sm"
            variant="outline"
            onClick={async () => {
              await post("/api/dev/identity", { userId: u.id });
              enterApp();
            }}
          >
            <UserRound /> Continue as {u.displayName}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { busy, error, run } = useSubmit();
  return (
    <AuthCard title="Welcome back" subtitle={<>Sign in to {APP_NAME}. Used the previous {APP_NAME}? Use the same email and password.</>}>
      <form className="space-y-4" onSubmit={run(async () => {
        await post("/api/auth/login", { email, password });
        track("login");
        enterApp();
      })}>
        <Field id="email" label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" autoFocus />
        <Field id="password" label="Password" type="password" value={password} onChange={setPassword} autoComplete="current-password" />
        <div className="text-right text-sm">{linkTo("/forgot-password", "Forgot your password?")}</div>
        <Problem text={error} />
        <Button type="submit" className="w-full" disabled={busy}>
          {busy && <LoaderCircle className="animate-spin" />} Sign in
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">New here? {linkTo(`/signup${window.location.search}`, "Create an account")}</p>
      <DevUsers />
    </AuthCard>
  );
}

export function SignupPage() {
  const config = useConfig();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { busy, error, run } = useSubmit();
  const wanted = params().get("plan");
  const plan = wanted && wanted !== "free" ? planById(wanted) : null;
  useEffect(() => track("signup_start", plan ? { plan: plan.id } : undefined), [plan?.id]);
  const invite = currentInvite();
  const inviter = useQuery({
    queryKey: ["invitation", invite],
    queryFn: () => get<{ firstName: string }>(`/api/invitation?code=${encodeURIComponent(invite!)}`),
    enabled: !!invite,
    retry: false,
  });
  return (
    <AuthCard
      title="Create your account"
      subtitle={
        plan ? (
          config.data?.paymentsOpen ? (
            <>First your account, then the payment page for {plan.name} (by Stripe).</>
          ) : (
            <>You start on Free with everything included. You can move to {plan.name} from Settings at any time.</>
          )
        ) : (
          <>Free up to 1 GB, with every feature. No card needed.</>
        )
      }
    >
      <form className="space-y-4" onSubmit={run(async () => {
        await post("/api/auth/signup", { name, email, password, invite: inviter.data ? invite : null });
        forgetInvite();
        track("signup_done", plan ? { plan: plan.id, invited: !!inviter.data } : { invited: !!inviter.data });
        // A plan chosen on the pricing page: on to Stripe Checkout (when payments are open).
        const period = params().get("period") === "month" ? "month" : "year";
        if (plan && config.data?.paymentsOpen) return void choosePlan(plan.id, period);
        enterApp("/");
      })}>
        {inviter.data && (
          <p className="rounded-md border border-primary/30 bg-primary/5 p-2 text-sm" role="status">
            <strong>{inviter.data.firstName || "A colleague"} invited you.</strong> Once you confirm your email, you both receive {INVITATION_REWARD.credits} extra AI credits.
          </p>
        )}
        <Field id="name" label="Your name" value={name} onChange={setName} autoComplete="name" autoFocus />
        <Field id="email" label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" />
        <Field id="password" label="Password" type="password" value={password} onChange={setPassword} autoComplete="new-password" hint="At least 8 characters." />
        <Problem text={error} />
        <Button type="submit" className="w-full" disabled={busy}>
          {busy && <LoaderCircle className="animate-spin" />} Create account
        </Button>
        <p className="text-xs text-muted-foreground">
          By creating an account you accept the {linkTo("/terms", "terms")} and the {linkTo("/privacy", "privacy policy")}.
        </p>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">Already have an account? {linkTo("/login", "Sign in")}</p>
    </AuthCard>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useSubmit();
  if (sent)
    return (
      <AuthCard title="Check your email">
        <p className="text-sm">
          If an account exists for <strong>{email}</strong>, we have sent it a link to choose a new password. The link works for one hour.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">Nothing after a few minutes? Look in your spam folder, or try again.</p>
        <p className="mt-6 text-center text-sm">{linkTo("/login", "Back to sign in")}</p>
      </AuthCard>
    );
  return (
    <AuthCard title="Reset your password" subtitle="Enter your email and we'll send you a link to choose a new password.">
      <form className="space-y-4" onSubmit={run(async () => {
        await post("/api/auth/forgot", { email });
        setSent(true);
      })}>
        <Field id="email" label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" autoFocus />
        <Problem text={error} />
        <Button type="submit" className="w-full" disabled={busy}>
          {busy && <LoaderCircle className="animate-spin" />} Send the link
        </Button>
      </form>
      <p className="mt-6 text-center text-sm">{linkTo("/login", "Back to sign in")}</p>
    </AuthCard>
  );
}

export function ResetPage() {
  const token = params().get("token") ?? "";
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const { busy, error, run } = useSubmit();
  return (
    <AuthCard title="Choose a new password" subtitle="You will be signed in, and signed out everywhere else.">
      <form className="space-y-4" onSubmit={run(async () => {
        if (password !== again) throw new Error("The two passwords are not the same.");
        await post("/api/auth/reset", { token, password });
        enterApp("/");
      })}>
        <Field id="password" label="New password" type="password" value={password} onChange={setPassword} autoComplete="new-password" hint="At least 8 characters." autoFocus />
        <Field id="again" label="Repeat the new password" type="password" value={again} onChange={setAgain} autoComplete="new-password" />
        <Problem text={!token ? "This link is incomplete. Open the link from the email again, or ask for a new one." : error} />
        <Button type="submit" className="w-full" disabled={busy || !token}>
          {busy && <LoaderCircle className="animate-spin" />} Save and sign in
        </Button>
      </form>
      <p className="mt-6 text-center text-sm">{linkTo("/forgot-password", "Ask for a new link")}</p>
    </AuthCard>
  );
}

export function VerifyPage({ signedIn }: { signedIn: boolean }) {
  const [state, setState] = useState<{ ok: boolean; message: string } | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = params().get("token") ?? "";
    post<{ email: string }>("/api/auth/verify", { token })
      .then((r) => setState({ ok: true, message: `${r.email} is confirmed. Thank you!` }))
      .catch((err) => setState({ ok: false, message: errorMessage(err) }));
  }, []);
  return (
    <AuthCard title={state?.ok ? "Email confirmed" : state ? "This link didn't work" : "Confirming…"}>
      {!state && <LoaderCircle className="animate-spin text-muted-foreground" />}
      {state && (
        <p className="flex items-start gap-2 text-sm" role="status">
          {state.ok && <CircleCheck className="mt-0.5 size-4 shrink-0 text-success" />} {state.message}
        </p>
      )}
      {state && (
        <Button className="mt-6 w-full" onClick={() => enterApp(signedIn ? "/" : "/login")}>
          {signedIn ? `Continue to ${APP_NAME}` : "Sign in"}
        </Button>
      )}
    </AuthCard>
  );
}
